import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, User, LoginCredentials } from '@/types';
import { getAxiosInstance } from '@/config/axios';
import { clearAllAppData, resetAllApiCaches } from '../utils/resetApiCache';
import type { AppDispatch } from '../index';

// Initial state
const getInitialState = (): AuthState => {
  const token = localStorage.getItem('token');
  const refreshToken = localStorage.getItem('refreshToken');
  return {
    user: null,
    token,
    refreshToken,
    isAuthenticated: !!token, // Set to true if token exists
    loading: false, // Don't set loading initially, let verification trigger it
    switchingContext: false, // Separate loading state for store/outlet switching
    error: null,
  };
};

const initialState: AuthState = getInitialState();

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue, dispatch }) => {
    try {
      // Ensure we start with a clean slate by clearing any existing cache
      resetAllApiCaches(dispatch as AppDispatch);

      const axios = getAxiosInstance(); // Get fresh axios instance with current hostname
      const response = await axios.post('/auth/login', credentials);
      const { user, tokens } = response.data.data;
      const token = tokens.accessToken;
      const refreshToken = tokens.refreshToken;

      // Store tokens in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken);

      return { token, refreshToken, user };
    } catch (error: any) {
      // Always return generic error message for security
      return rejectWithValue(
        'Login gagal. Periksa kembali kode toko, username dan password anda'
      );
    }
  }
);

export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      let token = state.auth.token;

      // Fallback to localStorage if token not in state
      if (!token) {
        token = localStorage.getItem('token');
      }

      if (!token) {
        throw new Error('No token found');
      }

      const axios = getAxiosInstance(); // Get fresh axios instance with current hostname
      const response = await axios.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      return response.data.data;
    } catch (error: any) {
      localStorage.removeItem('token');
      return rejectWithValue(
        error.response?.data?.message || 'Token verification failed'
      );
    }
  }
);

export const logoutUser = createAsyncThunk<void, void, { dispatch: AppDispatch }>(
  'auth/logout',
  async (_, { dispatch }) => {
    try {
      const axios = getAxiosInstance();
      await axios.post('/auth/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      console.warn('Logout API call failed, continuing with local cleanup');
    }

    // Clear all application data including RTK Query caches and localStorage
    clearAllAppData(dispatch);
  }
);

export const switchStore = createAsyncThunk(
  'auth/switchStore',
  async (storeId: string, { rejectWithValue }) => {
    try {
      const axios = getAxiosInstance();
      const response = await axios.post('/auth/switch-store', { storeId });
      const { tokens, currentStore, currentOutlet } = response.data.data;

      // Store new tokens
      localStorage.setItem('token', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);

      return {
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        currentStore,
        currentOutlet
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to switch store'
      );
    }
  }
);

export const switchOutlet = createAsyncThunk(
  'auth/switchOutlet',
  async (outletId: string, { rejectWithValue }) => {
    try {
      const axios = getAxiosInstance();
      const response = await axios.post('/auth/switch-outlet', { outletId });
      const { tokens, currentOutlet } = response.data.data;

      // Store new tokens
      localStorage.setItem('token', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);

      return {
        token: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        currentOutlet
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to switch outlet'
      );
    }
  }
);

// Auth slice
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      // Clear store/outlet selection
      localStorage.removeItem('selectedStoreId');
      localStorage.removeItem('selectedStoreName');
      localStorage.removeItem('selectedOutletId');
      localStorage.removeItem('selectedOutletName');
    },
    updateCurrentStore: (state, action: PayloadAction<{ id: string; name: string }>) => {
      if (state.user) {
        state.user.currentStore = action.payload;
      }
    },
    updateCurrentOutlet: (state, action: PayloadAction<{ id: string; name: string }>) => {
      if (state.user) {
        state.user.currentOutlet = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        state.user = action.payload.user;

        // Use currentStore and currentOutlet from backend response if available
        const stores = state.user?.stores;
        const outlets = state.user?.outlets;

        // Backend now provides currentStore and currentOutlet in login response
        if (state.user?.currentStore) {
          localStorage.setItem('selectedStoreId', state.user.currentStore.id);
          localStorage.setItem('selectedStoreName', state.user.currentStore.name);
        } else if (state.user && !state.user.currentStore && stores && stores.length > 0) {
          // Fallback: Auto-set default currentStore if not provided by backend
          const firstActiveStore = stores.find(s => s.isActive) || stores[0];
          state.user.currentStore = {
            id: firstActiveStore.id,
            name: firstActiveStore.name,
          };
          localStorage.setItem('selectedStoreId', firstActiveStore.id);
          localStorage.setItem('selectedStoreName', firstActiveStore.name);
        }

        if (state.user?.currentOutlet) {
          localStorage.setItem('selectedOutletId', state.user.currentOutlet.id);
          localStorage.setItem('selectedOutletName', state.user.currentOutlet.name);
        } else if (state.user && !state.user.currentOutlet && outlets && outlets.length > 0 && state.user.currentStore) {
          // Fallback: Auto-set default currentOutlet if not provided by backend
          const storeOutlets = outlets.filter(o => o.storeId === state.user!.currentStore!.id && o.isActive);
          if (storeOutlets.length > 0) {
            state.user.currentOutlet = {
              id: storeOutlets[0].id,
              name: storeOutlets[0].name,
            };
            localStorage.setItem('selectedOutletId', storeOutlets[0].id);
            localStorage.setItem('selectedOutletName', storeOutlets[0].name);
          }
        }

        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = action.payload as string;
      });

    // Verify token
    builder
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload;

        // Restore session from localStorage if available (to match UI state)
        const savedStoreId = localStorage.getItem('selectedStoreId');
        const savedStoreName = localStorage.getItem('selectedStoreName');
        const savedOutletId = localStorage.getItem('selectedOutletId');
        const savedOutletName = localStorage.getItem('selectedOutletName');

        const stores = state.user?.stores;
        const outlets = state.user?.outlets;

        if (state.user) {
          // Restore store from localStorage if valid
          if (savedStoreId && savedStoreName) {
            // Validate that saved store still exists in user's stores
            const savedStoreValid = stores?.some(s => s.id === savedStoreId);
            if (savedStoreValid) {
              state.user.currentStore = { id: savedStoreId, name: savedStoreName };
            } else {
              // Clear invalid store from localStorage
              localStorage.removeItem('selectedStoreId');
              localStorage.removeItem('selectedStoreName');
            }
          }

          // Restore outlet from localStorage if valid AND belongs to current store
          if (savedOutletId && savedOutletName && state.user.currentStore) {
            // Validate that saved outlet exists AND belongs to current store
            const savedOutletValid = outlets?.some(
              o => o.id === savedOutletId && o.storeId === state.user!.currentStore!.id
            );
            if (savedOutletValid) {
              state.user.currentOutlet = { id: savedOutletId, name: savedOutletName };
            } else {
              // Clear invalid outlet from localStorage (stale data from different store)
              localStorage.removeItem('selectedOutletId');
              localStorage.removeItem('selectedOutletName');
            }
          }
        }

        // Auto-set default currentStore and currentOutlet if not provided
        if (state.user && !state.user.currentStore && stores && stores.length > 0) {
          const firstActiveStore = stores.find(s => s.isActive) || stores[0];
          state.user.currentStore = {
            id: firstActiveStore.id,
            name: firstActiveStore.name,
          };

          // PERSIST to localStorage so storeSlice can read it
          localStorage.setItem('selectedStoreId', firstActiveStore.id);
          localStorage.setItem('selectedStoreName', firstActiveStore.name);
        }

        // Auto-set default outlet if not set but store is set
        if (state.user && state.user.currentStore && !state.user.currentOutlet && outlets && outlets.length > 0) {
          const storeOutlets = outlets.filter(o => o.storeId === state.user!.currentStore!.id && o.isActive);
          if (storeOutlets.length > 0) {
            state.user.currentOutlet = {
              id: storeOutlets[0].id,
              name: storeOutlets[0].name,
            };

            // PERSIST to localStorage so storeSlice can read it
            localStorage.setItem('selectedOutletId', storeOutlets[0].id);
            localStorage.setItem('selectedOutletName', storeOutlets[0].name);
          }
        }

        state.error = null;
      })
      .addCase(verifyToken.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        state.error = null; // Don't show error for token verification
        // Clear store/outlet selection
        localStorage.removeItem('selectedStoreId');
        localStorage.removeItem('selectedStoreName');
        localStorage.removeItem('selectedOutletId');
        localStorage.removeItem('selectedOutletName');
      });

    // Logout
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.loading = false;
        state.error = null;
      });

    // Switch Store
    builder
      .addCase(switchStore.pending, (state) => {
        state.switchingContext = true;
        state.error = null;
      })
      .addCase(switchStore.fulfilled, (state, action) => {
        state.switchingContext = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        if (state.user) {
          state.user.currentStore = action.payload.currentStore;
          state.user.currentOutlet = action.payload.currentOutlet;
        }
        state.error = null;
      })
      .addCase(switchStore.rejected, (state, action) => {
        state.switchingContext = false;
        state.error = action.payload as string;
      });

    // Switch Outlet
    builder
      .addCase(switchOutlet.pending, (state) => {
        state.switchingContext = true;
        state.error = null;
      })
      .addCase(switchOutlet.fulfilled, (state, action) => {
        state.switchingContext = false;
        state.token = action.payload.token;
        state.refreshToken = action.payload.refreshToken;
        if (state.user) {
          state.user.currentOutlet = action.payload.currentOutlet;
        }
        state.error = null;
      })
      .addCase(switchOutlet.rejected, (state, action) => {
        state.switchingContext = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setUser, clearAuth, updateCurrentStore, updateCurrentOutlet } = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;
export const selectSwitchingContext = (state: { auth: AuthState }) => state.auth.switchingContext;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;

// Store/Outlet Selectors
export const selectCurrentStore = (state: { auth: AuthState }) => state.auth.user?.currentStore || null;
export const selectCurrentOutlet = (state: { auth: AuthState }) => state.auth.user?.currentOutlet || null;
export const selectUserStores = (state: { auth: AuthState }) => state.auth.user?.stores || [];
export const selectUserOutlets = (state: { auth: AuthState }) => state.auth.user?.outlets || [];
export const selectUserPermissions = (state: { auth: AuthState }) => state.auth.user?.permissions || [];
